import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import {
  useGetAPAgingSummaryInsightsQuery,
  useGetARAgingSummaryInsightsQuery,
} from "store/apis/financialMetrics";
import {
  AccountsCard,
  MetricItem,
  MetricsDivider,
} from "./AccountsAnalysisCard";
import { formatAmount } from "utils/foramtAmount";

const AgingPeriodsTable = ({
  periods,
}: {
  periods: { label: string; amount: number }[];
}) => (
  <div className="t-flex t-flex-col">
    <div className="t-flex t-justify-between t-py-2">
      <span className="t-text-body-sm t-text-text-30">Period</span>
      <span className="t-text-body-sm t-text-text-30">Amounts</span>
    </div>
    {periods.map((period) => (
      <div
        key={period.label}
        className="t-flex t-justify-between t-items-center t-py-1.5"
      >
        <span className="t-text-body-sm t-text-text-60">{period.label}</span>
        <span className="t-text-body-sm t-text-text-60">
          {formatAmount(period.amount)}
        </span>
      </div>
    ))}
  </div>
);

type AgingCardProps = {
  title: string;
  emptyTitle: string;
  emptySubtitle: string;
  currentAmount: number;
  overdueAmount: number;
  periods: { label: string; amount: number }[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
};

const AgingCard = ({
  title,
  emptyTitle,
  emptySubtitle,
  currentAmount,
  overdueAmount,
  periods,
  isLoading,
  isSuccess,
  isError,
}: AgingCardProps) => (
  <AccountsCard
    title={title}
    loadingText={`Loading ${title}...`}
    emptyTitle={emptyTitle}
    emptySubtitle={emptySubtitle}
    isLoading={isLoading}
    isSuccess={isSuccess}
    isError={isError}
    isEmpty={currentAmount === 0 && overdueAmount === 0}
  >
    <div className="t-flex t-gap-6 t-justify-between t-items-center">
      <div className="t-flex t-gap-6 t-justify-between t-items-center t-w-full">
        <MetricItem label="Current" amount={currentAmount} />
        <MetricsDivider />
      </div>
      <div className="t-w-full">
        <MetricItem label="Overdue" amount={overdueAmount} />
      </div>
    </div>
    <AgingPeriodsTable periods={periods} />
  </AccountsCard>
);

type AgingCardPublicProps = {
  startDate: string;
  endDate: string;
};

export const ARAgingCard = ({ startDate, endDate }: AgingCardPublicProps) => {
  const entityId = useCurrentEntityId();

  const { data, isLoading, isSuccess, isError } =
    useGetARAgingSummaryInsightsQuery(
      { entityId, start_date: startDate, end_date: endDate },
      { skip: !entityId }
    );

  return (
    <AgingCard
      title="AR Aging"
      emptyTitle="No outstanding Receivables"
      emptySubtitle="Create invoices to track Accounts Receivable"
      currentAmount={data?.current_amount ?? 0}
      overdueAmount={data?.overdue_amount ?? 0}
      periods={data?.periods ?? []}
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
    />
  );
};

export const APAgingCard = ({ startDate, endDate }: AgingCardPublicProps) => {
  const entityId = useCurrentEntityId();

  const { data, isLoading, isSuccess, isError } =
    useGetAPAgingSummaryInsightsQuery(
      { entityId, start_date: startDate, end_date: endDate },
      { skip: !entityId }
    );

  return (
    <AgingCard
      title="AP Aging"
      emptyTitle="No outstanding Payables"
      emptySubtitle="Create bills to track Accounts Payables"
      currentAmount={data?.current_amount ?? 0}
      overdueAmount={data?.overdue_amount ?? 0}
      periods={data?.periods ?? []}
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
    />
  );
};
